type JsonObject = Record<string, unknown>;

export type TeamsConnectionFormState = {
  appId: string;
  appPassword: string;
  tenantId: string;
  webhookPort: string;
  webhookPath: string;
};

const KNOWN_KEYS = new Set([
  "appId",
  "appPassword",
  "tenantId",
  "webhookPort",
  "webhookPath",
]);

function isRecord(value: unknown): value is JsonObject {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function readString(value: unknown): string {
  return typeof value === "string" ? value : "";
}

export function createDefaultTeamsConnectionFormState(): TeamsConnectionFormState {
  return {
    appId: "",
    appPassword: "",
    tenantId: "",
    webhookPort: "",
    webhookPath: "",
  };
}

export function parseTeamsConnectionConfig(
  config: unknown,
): TeamsConnectionFormState {
  const base = createDefaultTeamsConnectionFormState();
  if (!isRecord(config)) {
    return base;
  }

  return {
    appId: readString(config.appId),
    appPassword: readString(config.appPassword),
    tenantId: readString(config.tenantId),
    webhookPort:
      typeof config.webhookPort === "number" && Number.isFinite(config.webhookPort)
        ? String(config.webhookPort)
        : readString(config.webhookPort),
    webhookPath: readString(config.webhookPath),
  };
}

export function buildTeamsConnectionConfig(
  state: TeamsConnectionFormState,
  existingConfig?: unknown,
): JsonObject {
  const preserved: JsonObject = {};
  if (isRecord(existingConfig)) {
    for (const [key, value] of Object.entries(existingConfig)) {
      if (!KNOWN_KEYS.has(key)) {
        if (value !== undefined && value !== null) {
          preserved[key] = value;
        }
      }
    }
  }

  const config: JsonObject = { ...preserved };

  const assignString = (key: string, value: string) => {
    const trimmed = value.trim();
    if (trimmed) {
      config[key] = trimmed;
    }
  };

  const assignNumber = (key: string, value: string) => {
    const trimmed = value.trim();
    if (!trimmed) {return;}
    const parsed = Number(trimmed);
    if (!Number.isFinite(parsed) || parsed < 0) {
      throw new Error(`${key} must be a valid non-negative number.`);
    }
    config[key] = parsed;
  };

  assignString("appId", state.appId);
  assignString("appPassword", state.appPassword);
  assignString("tenantId", state.tenantId);
  assignNumber("webhookPort", state.webhookPort);
  assignString("webhookPath", state.webhookPath);

  return config;
}
